/**
 * 充值模块 API 接口
 * 对应 OpenAPI: /deposit/*
 */

import { get, post } from '../client';
import type { ApiResult, PaginationParams } from '../types/common';
import type {
  PlanWithMethods,
  CreateDepositOrderRequest,
  CreateDepositOrderResult,
  BlockchainConfig,
  DepositOrder,
  DepositOrderListResult,
} from './deposit';

// ==================== 充值计划 ====================

/**
 * 获取充值计划列表
 *
 * 返回所有启用的订阅计划及其支持的支付方式（含链 / 代币信息）。
 *
 * @returns 充值计划列表
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function getDepositPlans(): Promise<ApiResult<PlanWithMethods[]>> {
  return get<PlanWithMethods[]>('/deposit/plans', { requiresAuth: true });
}

// ==================== 区块链网络 ====================

/**
 * 获取已启用的链网络列表
 *
 * 后端仅返回 status === 1 的记录（不含禁用），供前端展示网络选择。
 * **匿名可读**：登录页冷启动需拉取当前链；若后端仍要求 Bearer，需与后端对齐后改回 `requiresAuth: true` 并改用登录后拉取策略。
 *
 * @returns 区块链配置列表
 * @requiresAuth 否（可选 Bearer，有 token 时仍会带上）
 *
 * @example
 * ```ts
 * const res = await getActiveBlockchains();
 * if (res.ok) {
 *   res.data.forEach(chain => console.log(chain.chainName));
 * }
 * ```
 */
export async function getActiveBlockchains(): Promise<ApiResult<BlockchainConfig[]>> {
  return get<BlockchainConfig[]>('/deposit/blockchains', { requiresAuth: false });
}

// ==================== 充值订单 ====================

/**
 * 创建充值订单
 *
 * 指定充值金额和链配置 ID，系统返回对应的充值合约地址 (`contractAddress`)
 * 及代币信息。用户将代币转账至该地址后，链上监听服务自动确认并入账。
 *
 * @param data.amount 充值金额（USDT 字符串，最多 6 位小数）
 * @param data.chainConfigId 链配置 ID（可从 /deposit/plans 的 paymentMethods.blockchainId 获取）
 * @param data.subscriptionOrderNo 可选，关联的订阅订单号（`sub_<uuid>`）；
 *   传入后到账并余额充足时后端会自动履约该订阅订单（subscribe/upgrade/downgrade）。
 *   若订单不存在或非 pending，接口返回 400。
 * @returns 充值订单（含 contractAddress / tokenAddress / tokenDecimals / chainId / expiresAt）
 * @requiresAuth 需要 Bearer Token 认证
 *
 * @example
 * ```ts
 * const res = await createDepositOrder({ amount: '100.00', chainConfigId: 1 });
 * if (res.ok) {
 *   const { contractAddress, tokenAddress, tokenDecimals, amount } = res.data;
 *   // 调用钱包向 contractAddress 转入对应数量的 USDT
 * }
 * ```
 */
export async function createDepositOrder(
  data: CreateDepositOrderRequest
): Promise<ApiResult<CreateDepositOrderResult>> {
  return post<CreateDepositOrderResult>('/deposit/orders', data, {
    requiresAuth: true,
  });
}

/**
 * 获取 USDT 充值订单列表（分页）
 *
 * 对应 OpenAPI: GET /deposit/orders
 *
 * 返回结构与创建响应不同，使用 {@link DepositOrder} 类型（含 userId / receivedAmount / txHash 等）。
 *
 * @param params.page 页码（可选，从 1 开始）
 * @param params.limit 每页条数（可选）
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function getDepositOrders(
  params?: Pick<PaginationParams, 'page' | 'limit'>
): Promise<ApiResult<DepositOrderListResult>> {
  const search = new URLSearchParams();
  if (params?.page) search.set('page', String(params.page));
  if (params?.limit) search.set('limit', String(params.limit));
  const qs = search.toString();
  const url = qs ? `/deposit/orders?${qs}` : '/deposit/orders';
  return get<DepositOrderListResult>(url, { requiresAuth: true });
}

/**
 * 查询单个充值订单详情
 *
 * 对应 OpenAPI: GET /deposit/orders/{id}（path 参数为 integer）
 *
 * @param id 订单的数字 ID
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function getDepositOrderDetail(
  id: number
): Promise<ApiResult<DepositOrder>> {
  return get<DepositOrder>(`/deposit/orders/${id}`, { requiresAuth: true });
}
